import { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import useAuth from '@hooks/use-auth';
import DashboardContent from '../app/(dashboard)/content';
import { SidebarProvider } from '../contexts/sidebar-context';
import { PPJKProvider, usePPJKContext } from '@contexts/PPJKContext';
import { CompanyFeaturesProvider } from '@contexts/CompanyFeaturesContext';
import { PermissionsProvider } from '@contexts/PermissionsContext';
import ModuleGuard from '@components/feature-lock/ModuleGuard';
import MenuAccessGuard from '@components/permission-guard/MenuAccessGuard';
import ReadOnlyNotice from '@components/permission-guard/ReadOnlyNotice';
import { CeisaNotificationProvider } from '@components/notifications/CeisaNotificationProvider';
import SystemNotificationBanner from '@components/notifications/SystemNotificationBanner';
import { useNotificationWebSocket } from '@hooks/useNotificationWebSocket';
import { Loader, Center } from '@mantine/core';
import { useTheme } from '@contexts/ThemeContext';
import { TourProvider } from '@/features/onboarding';
import { PatchLogGate, PatchLogGateProvider } from '@/features/patch-logs';
import { OnboardingGate, OnboardingGateProvider } from '@/features/first-login';

export default function DashboardLayout() {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Add a small delay to prevent blinking during auth check
    const timer = setTimeout(() => {
      setLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <PPJKProvider>
      <CompanyFeaturesProvider>
        <PermissionsProvider>
          <DashboardLayoutContent />
        </PermissionsProvider>
      </CompanyFeaturesProvider>
    </PPJKProvider>
  );
}

// Inner component that can access PPJK context
function DashboardLayoutContent() {
  const { nomorPpjk } = usePPJKContext();
  const { colors: themeColors } = useTheme();
  const [wsKey, setWsKey] = useState(0);
  const { notification, dismiss } = useNotificationWebSocket();

  // Listen for impersonation changes to reconnect WebSocket
  useEffect(() => {
    const handleImpersonationChange = () => {
      setWsKey((prev) => prev + 1); // Force WebSocket to reconnect with new PPJK
    };

    window.addEventListener('impersonationChanged', handleImpersonationChange);
    return () => {
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  return (
    <CeisaNotificationProvider key={wsKey} ppjkNpwp={nomorPpjk || undefined}>
      <SystemNotificationBanner notification={notification} onDismiss={dismiss} />
      <SidebarProvider>
        <PatchLogGateProvider>
          <OnboardingGateProvider>
            <TourProvider>
              {/* First thing on screen after login when there are unread patch logs. */}
              <PatchLogGate />
              {/* Then, for accounts that are new to Jarvis, the first-login wizard. */}
              <OnboardingGate />
              <div
                style={{
                  backgroundColor: themeColors.background,
                  backgroundImage: themeColors.backgroundGradient,
                  backgroundAttachment: themeColors.backgroundGradient ? 'fixed' : undefined,
                }}
              >
                <DashboardContent>
                  <ModuleGuard>
                    <MenuAccessGuard>
                      <ReadOnlyNotice />
                      <Outlet />
                    </MenuAccessGuard>
                  </ModuleGuard>
                </DashboardContent>
              </div>
            </TourProvider>
          </OnboardingGateProvider>
        </PatchLogGateProvider>
      </SidebarProvider>
    </CeisaNotificationProvider>
  );
}
